import gsap from "gsap";
import { useRef, useState, useEffect } from "react";
// import Button from "./Button";
import AnimatedTitle from "./AnimatedTitle";

const FloatingImage = () => {
  const frameRef = useRef(null);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoInView, setIsVideoInView] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);

  // Preload video when component mounts
  useEffect(() => {
    const preloadVideo = () => {
      const videoElement = document.createElement('video');
      videoElement.src = "https://storage.googleapis.com/tll-videos-for-web/ContactVideoPre.mp4";
      videoElement.preload = 'metadata';
      videoElement.addEventListener('loadedmetadata', () => {
        console.log('[FloatingImage] Video preloaded successfully');
        setIsPreloaded(true);
      });
      videoElement.load();
    };
    
    // Delay preloading slightly to prioritize initial page load
    const timer = setTimeout(preloadVideo, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isInView = entry.isIntersecting;
        
        if (isInView && entry.intersectionRatio > 0.1) {
          console.log('[FloatingImage] Section in view, loading video');
          setShouldLoadVideo(true);
          setIsVideoInView(true);
        } else if (!isInView && entry.intersectionRatio === 0) {
          console.log('[FloatingImage] Section out of view');
          setIsVideoInView(false);
        }
      },
      { 
        threshold: [0, 0.1, 0.5],
        rootMargin: '50px 0px' // Start loading slightly before it comes into view
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Control video playback based on visibility
  useEffect(() => {
    if (videoRef.current) {
      if (isVideoInView) {
        // Set preload to auto when about to play
        videoRef.current.preload = 'auto';
        
        // Load the video if not already loaded
        if (videoRef.current.readyState === 0) {
          videoRef.current.load();
        }
        
        const playVideo = () => {
          videoRef.current.play().catch(err => {
            console.log('[FloatingImage] Auto-play prevented:', err);
          });
        };

        if (videoRef.current.readyState >= 3) {
          // Video is already loaded enough to play
          playVideo();
        } else {
          // Wait for video to be ready
          const handleCanPlay = () => {
            playVideo();
          };
          videoRef.current.addEventListener('canplay', handleCanPlay, { once: true });
        }
      } else {
        videoRef.current.pause();
      }
    }
  }, [isVideoInView]);

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const element = frameRef.current;
    
    if (!element) return;
    
    const rect = element.getBoundingClientRect();
    const xPos = clientX - rect.left;
    const yPos = clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = ((yPos - centerY) / centerY) * -10;
    const rotateY = ((xPos - centerX) / centerX) * 10;
    
    gsap.to(element, {
      duration: 0.3,
      rotateX,
      rotateY,
      transformPerspective: 500,
      ease: "power1.inOut",
    });
  };

  const handleMouseLeave = () => {
    const element = frameRef.current;
    if (element) {
      gsap.to(element, {
        duration: 0.3,
        rotateX: 0,
        rotateY: 0,
        ease: "power1.inOut",
      });
    }
  };

  const handleVideoLoad = () => {
    console.log('[FloatingImage] Video loaded successfully');
  };

  const handleVideoError = (e) => {
    console.error('[FloatingImage] Video load error:', e);
  };

  return (
    <div ref={sectionRef} id="story" className="min-h-dvh w-screen bg-black text-blue-50">
      <div className="flex size-full flex-col items-center py-10 pb-24">
        <p className="font-general text-sm uppercase md:text-[10px]">
          {/* Content here if needed */}
        </p>

        <div className="relative size-full">
          <AnimatedTitle
            title="Y<b>O</b>UR W<b>O</b>RKFLOWS REIM<b>A</b>GINED"
            containerClass="mt-5 pointer-events-none mix-blend-difference relative z-10"
          />

          <div className="story-img-container">
            <div className="story-img-mask">
              <div className="story-img-content">
                {shouldLoadVideo ? (
                  <video
                    ref={(el) => {
                      frameRef.current = el;
                      videoRef.current = el;
                    }}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseLeave}
                    onMouseEnter={handleMouseLeave}
                    src="https://storage.googleapis.com/tll-videos-for-web/ContactVideoPre.mp4"
                    className="size-full object-contain object-center"
                    autoPlay={isVideoInView}
                    loop
                    muted
                    playsInline
                    preload={isPreloaded ? "auto" : "metadata"}
                    onLoadedData={handleVideoLoad}
                    onError={handleVideoError}
                  />
                ) : (
                  <div className="size-full animate-pulse bg-gray-900" />
                )}
              </div>
            </div>

            {/* for the rounded corner */}
            <svg
              className="invisible absolute size-0"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <filter id="flt_tag">
                  <feGaussianBlur
                    in="SourceGraphic"
                    stdDeviation="8"
                    result="blur"
                  />
                  <feColorMatrix
                    in="blur"
                    mode="matrix"
                    values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 19 -9"
                    result="flt_tag"
                  />
                  <feComposite
                    in="SourceGraphic"
                    in2="flt_tag"
                    operator="atop"
                  />
                </filter>
              </defs>
            </svg>
          </div>
        </div>

        <div className="mt-3 flex w-full justify-center md:me-44 md:mt-4 md:justify-end">
          <div className="flex h-full w-fit flex-col items-center md:items-start">
            <p className="mt-3 max-w-sm text-center font-circular-web text-violet-50 md:text-start">
              Imagine your most time-consuming processes running automatically, your data 
              systems talking seamlessly, and your team focused on what matters most—while 
              systematic automation handles the rest. Transform your daily workflow chaos 
              into a structured flow, one purposeful connection at a time.
            </p>
            {/* <Button
              id="realm-btn"
              title="discover prologue"
              containerClass="mt-5"
            /> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingImage;